import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserIcon,
  UsersIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon,
  WifiIcon,
  SignalIcon,
  SignalSlashIcon,
  MapPinIcon,
  CreditCardIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import DeviceIcon from '../DeviceIcon';

const UserCard = ({ user }) => {
  const navigate = useNavigate();
  const online = user.isOnline;
  const subscriberData = user.subscriberData;
  const isOperator = user.role === 'operator';

  // Calculate device count from devices array
  const deviceCount = user.devices?.length || 0;

  // Two outcomes only, matching the user detail page: a free year grants the
  // same access a paid year does, so it reads as Active here.
  const getSubscriptionStatus = () => {
    const status = user.subscription?.status;
    return status === 'active' || status === 'trial'
      ? { color: 'green', text: 'Active', icon: CheckCircleIcon }
      : { color: 'gray', text: 'Inactive', icon: XCircleIcon };
  };

  const subscriptionStatus = getSubscriptionStatus();

  // Helper function to get connection time text (matching device cards exactly)
  const getLastSeenText = () => {
    if (online) {
      // For online users, show "Connected X ago" like device cards
      // Get connectionTime from subscriberData.sockets array
      const connectionTime = subscriberData?.sockets?.[0]?.connectionTime;
      if (connectionTime) {
        const timeAgo = formatDistanceToNow(new Date(connectionTime), { addSuffix: true });
        return `Connected ${timeAgo.replace('about ', '')}`;
      }
      return 'Currently active';
    }

    // For offline users, check various sources (priority order)
    // 1. lastSessionEnd - from DeviceSessionLog.subscribers.disconnectedAt
    if (user.lastSessionEnd) {
      const timeAgo = formatDistanceToNow(new Date(user.lastSessionEnd), { addSuffix: true });
      return timeAgo.replace('about ', '');
    }

    // 2. lastActivity - calculated from multiple sources
    if (user.lastActivity) {
      const timeAgo = formatDistanceToNow(new Date(user.lastActivity), { addSuffix: true });
      return timeAgo.replace('about ', '');
    }

    // 3. lastLogin - from User model
    if (user.lastLogin) {
      const timeAgo = formatDistanceToNow(new Date(user.lastLogin), { addSuffix: true });
      return timeAgo.replace('about ', '');
    }

    return 'Never connected';
  };

  return (
    <motion.div
      whileHover={{ y: -2 }}
      className="group"
      onClick={() => navigate(`/dashboard/users/${user._id}`)}
    >
      <div className={`relative bg-white rounded-xl transition-all duration-300 cursor-pointer overflow-hidden ${
        isOperator
          ? 'shadow-sm border border-indigo-200 hover:shadow-lg hover:border-indigo-400'
          : online
            ? 'shadow-md border-2 border-green-200 hover:shadow-xl hover:border-green-300'
            : 'shadow-sm border border-gray-200 hover:shadow-lg hover:border-blue-300'
      }`}>
        {/* Left accent stripe for operators — instant visual differentiation in lists */}
        {isOperator && (
          <div className="absolute top-0 bottom-0 left-0 w-1 bg-gradient-to-b from-indigo-400 to-indigo-600" />
        )}
        {/* Header Section */}
        <div className={`p-4 border-b ${
          isOperator
            ? 'bg-gradient-to-r from-indigo-50 to-white border-indigo-100'
            : online
              ? 'bg-gradient-to-r from-green-50 to-white border-green-100'
              : 'bg-gradient-to-r from-gray-50 to-white border-gray-100'
        }`}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                isOperator ? 'bg-indigo-100' : online ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                {isOperator ? (
                  <UsersIcon className="w-6 h-6 text-indigo-600" />
                ) : (
                  <UserIcon className={`w-6 h-6 ${online ? 'text-green-600' : 'text-gray-500'}`} />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 capitalize">{user.firstName} {user.lastName}</h3>
                {isOperator ? (
                  <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                      <UsersIcon className="w-3 h-3" />
                      Operator
                    </span>
                    {user.parent && (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                        <LinkIcon className="w-3 h-3 text-gray-400" />
                        under <span className="font-medium capitalize">{user.parent.firstName} {user.parent.lastName}</span>
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-600 capitalize">{user.role || 'User'}</p>
                )}
              </div>
            </div>
            {/* Status Badges */}
            <div className="flex items-center gap-2">
              {/* Annual access badge */}
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium bg-${subscriptionStatus.color}-100 text-${subscriptionStatus.color}-700`}>
                <div className="flex items-center gap-1">
                  <CreditCardIcon className="w-3 h-3" />
                  {subscriptionStatus.text}
                  {isOperator && user.inheritsFromOwner && (
                    <span className="ml-1 text-[10px] font-normal opacity-75">(inherited)</span>
                  )}
                </div>
              </span>
              {user.active === false && (
                <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-red-100 text-red-700">
                  Deactivated
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <BuildingOfficeIcon className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-700 capitalize">{user.farmName}</span>
          </div>
          
          <div className="flex items-center gap-2">
            <EnvelopeIcon className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-700">{user.email}</span>
          </div>

          {user.cellPhone && (
            <div className="flex items-center gap-2">
              <PhoneIcon className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-700">{user.cellPhone}</span>
            </div>
          )}

          {/* Device Count */}
          {deviceCount > 0 && (
            <div className="flex items-center gap-2">
              <DeviceIcon className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-700">
                {deviceCount} {deviceCount === 1 ? 'Device' : 'Devices'}
              </span>
            </div>
          )}

          {/* Address Information */}
          {user.address && (user.address.city || user.address.state || user.address.country) && (
            <div className="flex items-center gap-2">
              <MapPinIcon className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-700 truncate">
                {[
                  user.address.city,
                  user.address.state,
                  user.address.country
                ].filter(Boolean).join(', ')}
              </span>
            </div>
          )}

          {online && subscriberData && (
            <div className="flex items-center gap-2">
              <WifiIcon className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-700">
                {subscriberData.socketCount || 1} connection{subscriberData.socketCount !== 1 ? 's' : ''}, watching{' '}
                {subscriberData.subscriptions?.length || 0} device{subscriberData.subscriptions?.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        {/* Footer with Online Status (matching device card style exactly) */}
        <div className="px-4 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Status Icon and Text */}
              <div className="flex items-center gap-2">
                {online ? (
                  <SignalIcon className="w-4 h-4 text-green-600" />
                ) : (
                  <SignalSlashIcon className="w-4 h-4 text-gray-400" />
                )}
                <div className="flex flex-col">
                  <span className={`text-sm font-medium ${
                    online ? 'text-green-600' : 'text-gray-600'
                  }`}>
                    {online ? 'Online' : 'Offline'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {getLastSeenText()}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/dashboard/users/${user._id}`);
                }}
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                View Details →
              </button>
            </div>
          </div>
        </div>

      </div>
    </motion.div>
  );
};

export default UserCard;